import {
  Timestamp,
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import db, { BASE_FUNCTIONS_URL } from "../types/firebase";

export interface SecurityCodeEntry {
  id: string;
  codeType: string;
  createdAt: Timestamp | null;
  securityCode: string;
  workerCode: string;
}

type SecurityCodeLookupResponse = {
  data: {
    status: string;
    recognized?: boolean;
    securityCode?: string | null;
    createdAt?: string | null;
    message?: string;
  };
};

export type SecurityCodeLookupResult = {
  recognized: boolean;
  securityCode: string | null;
  createdAt: string | null;
  message: string;
};

export const loadRecentSecurityCodes = async (
  minutes: number = 20,
): Promise<SecurityCodeEntry[]> => {
  const cutoff = Timestamp.fromDate(
    new Date(Date.now() - minutes * 60 * 1000),
  );

  const securityCodesQuery = query(
    collection(db, "securityCodes"),
    where("createdAt", ">=", cutoff),
    orderBy("createdAt", "desc"),
  );

  const snapshot = await getDocs(securityCodesQuery);

  return snapshot.docs
    .map((securityCodeDoc) => {
      const data = securityCodeDoc.data();

      return {
        id: securityCodeDoc.id,
        codeType: (data.codeType as string | undefined) || "email",
        createdAt: (data.createdAt as Timestamp | undefined) || null,
        securityCode: (data.securityCode as string | undefined) || "",
        workerCode: (data.workerCode as string | undefined) || "",
      };
    })
    .filter((securityCode) => !!securityCode.createdAt);
};

export const lookupLatestSecurityCodeByEmail = async (
  email: string,
): Promise<SecurityCodeLookupResult> => {
  const apiKey = process.env.REACT_APP_FUNCTIONS_API_KEY || "";
  const response = await fetch(`${BASE_FUNCTIONS_URL}/public/lookup-security-code`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": apiKey,
    },
    body: JSON.stringify({ email }),
  });

  const responseText = await response.text();
  let responseData: SecurityCodeLookupResponse | null = null;

  if (responseText) {
    try {
      responseData = JSON.parse(responseText) as SecurityCodeLookupResponse;
    } catch (_error) {
      if (!response.ok) {
        throw new Error(responseText);
      }

      throw new Error("Server returned an invalid JSON response.");
    }
  }

  if (!response.ok) {
    throw new Error(responseData?.data?.message || responseText || "Request failed.");
  }

  return {
    recognized: Boolean(responseData?.data?.recognized),
    securityCode: responseData?.data?.securityCode || null,
    createdAt: responseData?.data?.createdAt || null,
    message: responseData?.data?.message || "",
  };
};
